import { useClerk } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { useLDClient } from "launchdarkly-react-client-sdk";
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useToast } from "@/components/ui/status/Toast/useToast";
import { RouteConfig } from "@/config/route.config";
import type { BizUserModels } from "@/data/bizUser/bizUser.models";
import { BizUserQueries } from "@/data/bizUser/bizUser.queries";
import { useClerkErrorHandler } from "@/hooks/useClerkErrorHandler";
import { logger } from "@/util/logger";
import { AppRestClient } from "@/util/rest/clients/app-rest-client";
import { AuthorizationHeaderInterceptor } from "@/util/rest/interceptors/authorization-header.interceptor";
import { TokenExpiredInterceptor } from "@/util/rest/interceptors/token-expired.interceptor";

export namespace AuthContext {
  export interface Type {
    isLoggedIn: boolean;
    user: BizUserModels.GetCurrentUserInfoResponseDto | undefined;
    isInitializing: boolean;
    isTokenApplied: boolean;
    shouldPerformSync: boolean;
    syncProfile: () => Promise<void>;
    logout: () => Promise<void>;
    updateUserProfilePicture: (profilePictureId: string, profilePictureUrl: string) => void;
    isAdmin: boolean;
    updateConfirmingInvitationRef: (value: boolean) => void;
    isClerkTokenApplied: boolean;
  }

  const Context = createContext<Type>({} as never);

  interface IProps {
    shouldPerformSync: boolean;
  }

  export const Provider = ({ children, shouldPerformSync }: PropsWithChildren<IProps>) => {
    const [user, setUser] = useState<BizUserModels.GetCurrentUserInfoResponseDto | undefined>(undefined);

    const { session, loaded: clerkLoaded, signOut, user: authUser, client: clerkClient } = useClerk();

    const [isClerkLoadingUp, setIsClerkLoadingUp] = useState<boolean>(false);
    const [isClerkLoggedIn, setIsClerkLoggedIn] = useState<boolean>(false);
    const [isClerkTokenApplied, setIsClerkTokenApplied] = useState<boolean>(false);
    const [isLoadingUserProfile, setIsLoadingUserProfile] = useState<boolean>(true);
    const [isUserLoggedInAndFetched, setIsUserLoggedInAndFetched] = useState<boolean>(false);

    const isSessionDefined = session !== null;

    const { refetch: refetchProfile } = BizUserQueries.useGetCurrentUserInfo({
      enabled: false,
    });

    useEffect(() => {
      if (!clerkLoaded) {
        setIsClerkLoadingUp(true);
      } else {
        setIsClerkLoadingUp(false);
        if (isSessionDefined) {
          setIsClerkLoggedIn(true);
        } else {
          setIsClerkLoggedIn(false);
          setIsUserLoggedInAndFetched(false);
          setIsLoadingUserProfile(false);
        }
      }
    }, [clerkLoaded, isSessionDefined]);

    const queryClient = useQueryClient();
    const confirmingInvitationRef = useRef<boolean>(false);
    const ldClient = useLDClient();
    // const [isLoggedIn, isLogedInRef, setIsLoggedIn] = useStateAndRef<boolean | undefined>(undefined);
    // const authUser = useUser();
    // const { isLoaded: isSessionLoaded } = useSession();
    // const [isLoadingAuthUser, setIsLoadingAuthUser] = useState<boolean>(false);
    // const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
    // const [accessTokenApplied, setAccessTokenApplied] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const { errorToast } = useToast();
    const { handleClerkError } = useClerkErrorHandler();
    const isSuspendedMessageShown = useRef(false);

    const resetAuthState = useCallback(() => {
      setUser(undefined);
      setIsLoadingUserProfile(false);
      setIsUserLoggedInAndFetched(false);
      setIsClerkTokenApplied(false);
      setIsClerkLoggedIn(false);
      confirmingInvitationRef.current = false;
      isSuspendedMessageShown.current = false;
    }, []);

    const logoutAsync = useCallback(async () => {
      // 1. Cancel ALL ongoing queries immediately
      await queryClient.cancelQueries();

      // 2. Remove all cached data to prevent stale queries
      queryClient.clear();

      // 3. Immediately disable auth state
      setIsClerkLoadingUp(true);
      resetAuthState();

      // 4. Remove interceptors to prevent token attachment or response handling
      AppRestClient.ejectInterceptor(AuthorizationHeaderInterceptor);
      AppRestClient.ejectInterceptor(TokenExpiredInterceptor);

      // 5. Then sign out and navigate
      await signOut({ redirectUrl: RouteConfig.signin });

      // 7. Destroy Clerk client
      await clerkClient.destroy();
    }, [signOut, resetAuthState, queryClient, clerkClient]);

    const syncProfile = useCallback(async () => {
      const userInfo = await refetchProfile();
      setUser(userInfo.data);
    }, [refetchProfile]);

    const updateUserProfilePicture = useCallback((profilePictureId: string, profilePictureUrl: string) => {
      setUser((userInfo) => ({
        ...userInfo!,
        profilePictureId,
        profilePictureUrl,
      }));
    }, []);

    const onAccessTokenExpired = useCallback(async () => {
      await logoutAsync();
    }, [logoutAsync]);

    const applyAccessToken = useCallback(
      (tokenSession: typeof session) => {
        AppRestClient.attachInterceptor(AuthorizationHeaderInterceptor, async () => {
          const token = await tokenSession?.getToken();
          return token ? `Bearer ${token}` : null;
        });
        AppRestClient.attachInterceptor(TokenExpiredInterceptor, onAccessTokenExpired);
        setIsClerkTokenApplied(true);
      },
      [onAccessTokenExpired],
    );

    useEffect(() => {
      if (!isClerkLoggedIn || !session) {
        return;
      }

      if (isClerkTokenApplied && isUserLoggedInAndFetched) {
        return;
      }

      setTimeout(() => {
        applyAccessToken(session);
      }, 200);
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [isClerkLoggedIn, session, isClerkTokenApplied, isUserLoggedInAndFetched]);

    useEffect(() => {
      if (!clerkLoaded || !isClerkLoggedIn || !isClerkTokenApplied) {
        return;
      }

      const tryRefetchProfile = async () => {
        try {
          const fetchedUser = await refetchProfile();
          if (fetchedUser.status === "error") {
            if (fetchedUser.error.serverMessage?.startsWith("No active organization found")) {
              if (!isSuspendedMessageShown.current) {
                errorToast({
                  text: "Your account has been suspended. Please contact your administrator.",
                });
                isSuspendedMessageShown.current = true;

                setTimeout(() => {
                  isSuspendedMessageShown.current = false;
                }, 5000);
              }
              await logoutAsync();
            }
            setIsUserLoggedInAndFetched(false);
            setIsLoadingUserProfile(false);
          }

          if (fetchedUser.data) {
            setUser(fetchedUser.data);
            setIsUserLoggedInAndFetched(true);
            setIsLoadingUserProfile(false);
            setIsAdmin(fetchedUser?.data?.organization?.role === "ADMIN");
          }
        } catch (error) {
          logger.error("Something went wrong while refetching the profile", error);
          // Handle potential Clerk-related errors during profile fetch
          handleClerkError(error, "tryRefetchProfile");
          setIsLoadingUserProfile(false);
        }
      };

      setIsLoadingUserProfile(true);
      setTimeout(() => {
        tryRefetchProfile();
      }, 400);
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [clerkLoaded, isClerkLoggedIn, isClerkTokenApplied]);

    const identifyUser = useCallback(
      (loggedAuthUser: typeof authUser) => {
        if (!loggedAuthUser) {
          return;
        }
        ldClient?.identify({
          key: loggedAuthUser?.emailAddresses[0].emailAddress,
          kind: "user",
          name: loggedAuthUser?.firstName ?? "",
          email: loggedAuthUser?.emailAddresses[0].emailAddress,
        });
      },
      [ldClient],
    );

    useEffect(() => {
      // we dientify user in LD once we have user data
      // this enables us to enable/disable flags per email address
      if (isUserLoggedInAndFetched && authUser) {
        identifyUser(authUser);
      }
    }, [identifyUser, isUserLoggedInAndFetched, authUser]);

    const updateConfirmingInvitationRef = useCallback(
      (value: boolean) => {
        confirmingInvitationRef.current = value;
      },
      [confirmingInvitationRef],
    );

    const value = useMemo(
      (): Type => ({
        isLoggedIn: isUserLoggedInAndFetched,
        user,
        isInitializing: isClerkLoadingUp || isLoadingUserProfile,
        isTokenApplied: isClerkTokenApplied,
        syncProfile,
        logout: logoutAsync,
        shouldPerformSync,
        isAdmin,
        updateConfirmingInvitationRef,
        updateUserProfilePicture,
        isClerkTokenApplied,
      }),
      [
        isUserLoggedInAndFetched,
        user,
        isClerkLoadingUp,
        isLoadingUserProfile,
        syncProfile,
        logoutAsync,
        shouldPerformSync,
        isAdmin,
        updateConfirmingInvitationRef,
        updateUserProfilePicture,
        isClerkTokenApplied,
      ],
    );

    let content = null;
    if (!isClerkLoadingUp) {
      content = children;
    }

    return <Context.Provider value={value}>{content}</Context.Provider>;
  };

  export const useAuth = () => {
    const auth = useContext(Context);
    return auth;
  };
}
